import { useEffect, useState } from "react";
import { IHall, IMember, IObject, ITable, ITableFreeTime } from "../App";
import { ISingleReservation, locale, t } from "../MainApp";
import { TableSelector } from "./TableSelector";
import { ReservationDatePicker } from "../sharedcomponents/ReservationDatePicker";
import HallOccupancyGraph from "../sharedcomponents/HallOccupancyGraph";
import { StartTimePicker } from "../sharedcomponents/StartTimePicker";
import { EndTimePicker } from "../sharedcomponents/EndTimePicker";
import { CommentComponent } from "../sharedcomponents/CommentComponent";
import { GuestsList } from "./GuestsList";
import { Button, Divider, notification, Typography } from "antd";
import { apiMakeReservation, apiUpdateReservation, makeTimestampFromDateAndDate } from "../api/API";
import dayjs, { Dayjs } from "dayjs";
import { apiFetchTableFreeTime } from "../api/API";
import { TimeChecker } from "../sharedcomponents/TimeChecker";
import { TableCanvas } from "./TableCanvas";

export type TSelectedTable = ITable | undefined;

type SingleHallReservationProps = {
  object: IObject;
  member: IMember;
  locale: locale;
  selectedHall: IHall;

  reservation: ISingleReservation | undefined;
  onMakeReservation: (rid: number) => void;
  onUpdateReservation: (rid: number) => void;
};

export function SingleHallReservation(props: SingleHallReservationProps) {
  let [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  let [startTime, setStartTime] = useState<Dayjs | null>(null);
  let [endTime, setEndTime] = useState<Dayjs | null>(null);
  let [selectedTable, setSelectedTable] = useState<TSelectedTable>(undefined);
  let [comment, setComment] = useState<string>('');

  let [guestNames, setGuestNames] = useState<string[]>([`@${props.member.username} - ${props.member.fullName}`]);
  let [isTheMemberGuest, setIsTheMemberGuest] = useState<boolean>(true);
  let [isTheChildGuest, setIsTheChildGuest] = useState<boolean>(false);

  let [selectedTableReservedTimes, setSelectedTableReservedTimes] = useState<ITableFreeTime[]>([]);

  const [api, contextHolder] = notification.useNotification({ placement: "topRight", maxCount: 1 });
  let [notificationErrorMessage, setNotificationErrorMessage] = useState<string>(``);

  useEffect( () => {
    if(notificationErrorMessage.length) {
      api.error({message: notificationErrorMessage});
    }
    setNotificationErrorMessage(``);
  }, [notificationErrorMessage]);

  useEffect(() => {
    if (props.reservation) {
      setSelectedDate(dayjs(props.reservation.startDate));
      setStartTime(dayjs(props.reservation.startDate));
      setEndTime(dayjs(props.reservation.endDate));
      setSelectedTable(() => {
        return props.selectedHall.tables.find( (value) => {
          return value.id === props.reservation?.tableId;
        });        
      });

      setIsTheMemberGuest(false);
      setIsTheChildGuest(false);

      for( let idx = 0; idx < props.reservation.guests.length; idx++ ) 
      {
        let guest = props.reservation.guests[idx];
        if( guest === `@${props.member.username} - ${props.member.fullName}`)
        {
          setIsTheMemberGuest(true);
        }
        else if( (guest === `@${props.member.username} - ${t('en', "thekid")}` ) || (guest === `@${props.member.username} - ${t('bg', "thekid")}`) )
        {
          setIsTheChildGuest(true);
        }
      }

      setGuestNames(props.reservation.guests);
      setComment(props.reservation.comment);
    }
  }, [props.member.fullName, props.member.username, props.reservation, props.selectedHall.tables]);

  useEffect(() => {
    if(!props.reservation) {
      if (canHaveStanding(props.selectedHall)) {
        setSelectedTable(undefined);
      } else {
        setSelectedTable(props.selectedHall.tables[0]);
      }  
    }
  }, [props.reservation, props.selectedHall]);

  useEffect(() => {
    if (selectedDate && startTime && endTime) {
      const copyOfSelectedDate = dayjs(selectedDate).toDate();
      copyOfSelectedDate.setHours(0, 0, 0, 0);

      const start = makeTimestampFromDateAndDate(copyOfSelectedDate, startTime.toDate());
      const end = makeTimestampFromDateAndDate(copyOfSelectedDate, endTime.toDate());

      if (start < end) {      
        // time is ok, check for table reservation conflicts
        if (selectedTable && !props.reservation) {
          apiFetchTableFreeTime(
            props.object.id,
            props.selectedHall.id,
            selectedTable.id,
            new Date(start),
            new Date(end),
          ).then((value) => {
            console.log(value);
            if (value.length > 0) {
              api.error({
                message: ``,
                description: t(props.locale, `tableConflictReserved`),
              });
            }
          });


        } else { // upstraight

        }
      }
    }
  }, [selectedDate, startTime, endTime, selectedTable]);

  useEffect(() => {
    if (selectedDate && selectedTable) {
      let startDate = selectedDate.toDate();
      startDate.setHours(0, 0, 0, 0);

      let endDate = selectedDate.toDate();
      endDate.setHours(23, 59, 59, 999);

      apiFetchTableFreeTime(
        props.object.id,
        props.selectedHall.id,
        selectedTable.id,
        startDate,
        endDate,
      ).then((value) => {
        setSelectedTableReservedTimes(value);
      });
    }
  }, [props.object.id, props.selectedHall.id, selectedDate, selectedTable]);

  useEffect(() => {
    let minGuests: number = 1;
    let maxGuests: number = 1;

    if (selectedTable) {
      maxGuests = selectedTable.seats;
    } else {
      if (canHaveStanding(props.selectedHall)) {
        maxGuests = props.selectedHall.rezervationSizeLimit;
      }
      else {
        // maxGuest remains 1
      }
    }

    if (guestNames.length > maxGuests) {
      setGuestNames((prevGuests) => {
        let newGuestsLength = Math.max(minGuests, maxGuests);

        let doesTheMemberStaysAsGuest = false;
        let doesTheChildStaysAsGuest = false;

        if (isTheMemberGuest) {
          doesTheMemberStaysAsGuest = newGuestsLength >= 1;
        }

        if (isTheChildGuest) {
          doesTheChildStaysAsGuest = newGuestsLength >= (isTheMemberGuest ? 2 : 1);
        }

        setIsTheMemberGuest(() => {
          setIsTheChildGuest(doesTheChildStaysAsGuest);
          return doesTheMemberStaysAsGuest;
        });

        return prevGuests.slice(0, newGuestsLength);
      });
    }
  }, [selectedTable]);

  function padToTwo(x: number) {
    let result: string = '';
    if (x < 10) {
      result += '0';
    }
    result += x;
    return result;
  }


  function tableReservationsTimeIntervals() {
    let reservedTimes: JSX.Element[] = [
      <span>{t(props.locale, "tableReservedForDay")}</span>
    ];

    for (let value of selectedTableReservedTimes) {
      let startDate: Date = new Date(value.startDate);
      let endDate: Date = new Date(value.endDate);
      reservedTimes.push(<div>{t(props.locale, "from")}: {padToTwo(startDate.getHours())}:{padToTwo(startDate.getMinutes())}  {t(props.locale, "to")}: {padToTwo(endDate.getHours())}:{padToTwo(endDate.getMinutes())}</div>);
    }

    return reservedTimes;
  }

  function canHaveStanding(hall: IHall): boolean {
    if (hall.rezervationSizeLimit > 0) {
      return true;
    } else {
      return false;
    }
  }


  function canAddSingleGuest(): boolean {
    if (selectedTable) {
      return selectedTable.seats > guestNames.length;
    }
    else {
      if (canHaveStanding(props.selectedHall)) {
        return props.selectedHall.rezervationSizeLimit > guestNames.length;
      }
      else {
        return false;
      }
    }
  }

  function onChangeSeats(value: number) {
    let oldLength = guestNames.length;
    let newLength = value;
    if (selectedTable) {
      if (newLength > selectedTable.seats) {
        newLength = selectedTable.seats;
      }
    }
    else {
      if (newLength > props.selectedHall.rezervationSizeLimit) {
        newLength = Math.max(props.selectedHall.rezervationSizeLimit, 1);
      }
    }

    if (newLength > oldLength) {
      setGuestNames((prevGuests) => {
        let newNames: string[] = [];
        let startIndexInclusive = guestNames.length + 1;
        let endIndexInclusive = newLength;
        for (let i = startIndexInclusive; i <= endIndexInclusive; ++i) {
          newNames.push(``);
        }
        return [...prevGuests, ...newNames];
      });
    }
    else if (newLength < oldLength) {
      setGuestNames((prevGuests) => {
        let newNames: string[] = [...prevGuests];
        for (let i = 0; i < (oldLength - newLength); ++i) {
          newNames.pop();
        }
        return newNames;
      });
    }
  }

  return (
    <>
      {contextHolder}

      <Divider><Typography.Title level={5}>{t(props.locale, "choosedate")}</Typography.Title></Divider>

      <ReservationDatePicker
        selectedDate={selectedDate}
        disabled={props.reservation ? true : false}
        locale={props.locale}
        object={props.object}
        onSelectedDateChange={(value) => {
          setSelectedDate(value)
          setStartTime(null);
          setEndTime(null);
        }}
      />

      {selectedDate ?
        <HallOccupancyGraph
          object={props.object}
          hall={props.selectedHall}
          date={selectedDate.toDate()}
        />
        : <></>}

      <Divider><Typography.Title level={5}>{t(props.locale, "from")}</Typography.Title></Divider>
      <StartTimePicker
        disabled={props.reservation ? true : false}
        format="HH:mm"
        object={props.object}
        selectedDate={selectedDate?.toDate()}
        reservationStartTime={startTime}
        onChangeReservationStartTime={(startTime) => setStartTime(startTime)}
      />

      <Divider><Typography.Title level={5}>{t(props.locale, "to")}</Typography.Title></Divider>

      <EndTimePicker
        format="HH:mm"
        disabled={props.reservation ? true : false}
        endTime={endTime}
        onChangeEndTime={(endTime) => setEndTime(endTime)}
        object={props.object}
        selectedDate={selectedDate?.toDate()}
        startTime={undefined}
      />

      {startTime && endTime
      ?      <TimeChecker
              start={startTime}
              end={endTime}
              locale={props.locale}
              />
      : <></>}

      <Divider><Typography.Title level={5}>{t(props.locale, "chooseTable")}</Typography.Title></Divider>
      <TableSelector
        disabled={false}
        locale={props.locale}
        onSelectTableId={(tableId) => setSelectedTable(() => {
          const table = props.selectedHall.tables.find((value) => {
            return value.id === tableId;
          });

          return table;
        })}
        selectedTable={selectedTable}
        tables={props.selectedHall.tables}
        hasStandingReservations={canHaveStanding(props.selectedHall)}
      />

      {selectedDate && selectedTable && selectedTableReservedTimes && selectedTableReservedTimes.length > 0 ? tableReservationsTimeIntervals() : <></>}

      {props.selectedHall.tables.length > 0 
      ? <TableCanvas
          disabled={false}
          object={props.object}
          selectedHall={props.selectedHall}
          selectedTable={selectedTable}
          onSelectTable={(table) => setSelectedTable(table)}
        />
      : <></>
      }
      

      <Divider><Typography.Title level={5}>{t(props.locale, "guests")}</Typography.Title></Divider>
      <GuestsList
        isTheMemberGuest={isTheMemberGuest}
        isTheChildGuest={isTheChildGuest}
        locale={props.locale}
        namesOfTheGuests={guestNames}
        onChangeGuestName={(index, name) => {
          setGuestNames((prevGuests) => {
            let copyOfGuestNames = [...prevGuests];
            copyOfGuestNames[index] = name;
            return copyOfGuestNames;
          });
        }}
        onRemoveGuest={(index) => {
          setGuestNames((prevGuests) => {
            let copyOfGuestNames = [...prevGuests];
            copyOfGuestNames.splice(index, 1);
            return copyOfGuestNames;
          });
        }}
        member={props.member}

        onAddMemberToGuestList={(swapChild) => {
          if (isTheMemberGuest) {
            return;
          }

          if (canAddSingleGuest() || (isTheChildGuest && swapChild)) {
            setGuestNames((prevGuests) => {
              let copyOfGuests = [...prevGuests];
              copyOfGuests.splice(0, 0, `@${props.member.username} - ${props.member.fullName}`);
              if (isTheChildGuest && swapChild) {
                copyOfGuests.splice(1, 1);
              }

              setIsTheMemberGuest(() => {
                setIsTheChildGuest(!(isTheChildGuest && swapChild));
                return true;
              });

              return copyOfGuests;
            });
          }
        }}

        onAddChildToGuestList={(swapParent) => {
          if (isTheChildGuest) {
            return;
          }

          if (canAddSingleGuest() || (isTheMemberGuest && swapParent)) {
            setGuestNames((prevGuests) => {
              let copyOfGuests = [...prevGuests];
              let childInsertIndex: number = isTheMemberGuest ? 1 : 0;
              copyOfGuests.splice(childInsertIndex, 0, `@${props.member.username} - ${t(props.locale, "thekid")}`);
              if (isTheMemberGuest && swapParent) {
                copyOfGuests.splice(0, 1);
              }

              setIsTheChildGuest(() => {
                setIsTheMemberGuest(!(isTheMemberGuest && swapParent));
                return true;
              });

              return copyOfGuests;
            });

          }
        }}

        onCanAddSingleGuest={() => canAddSingleGuest()}
        onAddGuest={() => { onChangeSeats(guestNames.length + 1); }}
        onRemoveMemberFromGuestList={() => {
          if (isTheMemberGuest && isTheChildGuest) {
            setGuestNames((prevGuests) => {
              let guests = [...prevGuests];
              guests.splice(0, 1);
              setIsTheMemberGuest(false);
              return guests;
            });
          }
        }}

        onRemoveChildFromGuestList={() => {
          if (!isTheChildGuest) {
            return;
          }

          if (isTheMemberGuest) {
            setGuestNames((prevGuests) => {
              let newGuests = [...prevGuests];
              let childIndexPosition: number = isTheMemberGuest ? 1 : 0;
              newGuests.splice(childIndexPosition, 1);

              setIsTheChildGuest(false);

              return newGuests;
            });

          }
          else {
            // swap child with the member
            setGuestNames((prevGuests) => {
              let newGuests = [...prevGuests];
              let childIndex: number = isTheMemberGuest ? 1 : 0;
              newGuests.splice(childIndex, 1);

              if (!isTheMemberGuest) {
                newGuests.splice(0, 0, `@${props.member.username} - ${props.member.fullName}`);
              }

              setIsTheChildGuest(() => {
                setIsTheMemberGuest(true);
                return false;
              });

              return newGuests;
            });
          }
        }}

        onBuildFullCapacityMessage={() => {
          return `${t(props.locale, 'fullcapacity')} ${t(props.locale, 'for')} ${selectedTable ? selectedTable.name : t(props.locale, "upstraight")}`;
        }}
      />

      <Divider><Typography.Title level={5}>{t(props.locale, "reservationcomment")}</Typography.Title></Divider>
      <CommentComponent
        comment={comment}
        locale={props.locale}
        onChangeComment={(comment) => setComment(comment)}
      />

      <Button style={{ width: '100%' }} type="primary" onClick={(event) => {
        event.preventDefault();
        if (selectedDate && startTime && endTime) {

          if (props.reservation === undefined) {
            apiMakeReservation(
              selectedDate,
              startTime,
              endTime,
              props.object.id,
              props.selectedHall.id,
              selectedTable ? selectedTable.id : undefined,
              guestNames,
              isTheMemberGuest,
              isTheChildGuest,
              comment)
              .then((value) => {
                if (value.status === 200 || value.status === 201) {
                  props.onMakeReservation(value.jsonResponse.reservationId);
                } else if (value.status === 409) {
                  setNotificationErrorMessage(t(props.locale, "tableConflictReserved"));
                } else {
                  if (value.jsonResponse) {
                    const response = value.jsonResponse;
                    setNotificationErrorMessage(response[props.locale]);
                  }
                }
              })
              .catch((reason) => {
                console.log(reason);
              });
          } else {
            apiUpdateReservation(
              props.reservation.id,
              guestNames,
              isTheMemberGuest,
              isTheChildGuest,
              selectedTable?.id,
              comment,
            ).then((value) => {
              if (value.status === 200) {
                if (props.reservation) {
                  props.onUpdateReservation(props.reservation?.id);
                } else {
                  // TODO
                }
              } else if (value.status === 409) {
                setNotificationErrorMessage(t(props.locale, "tableConflictReserved"));
              }
            }).catch((reason) => {
              console.log(reason);
              // TODO
            })
          }
        }



      }}>
        {t(props.locale, "confirm")}
      </Button>
    </>
  );
}